/**
 * Page lifecycle facts for the heartbeat (`visibility`, `idle`, `locked`), sleep/resume and
 * IP-change detection (a timer gap or an `online` event -> kick both reconnects), and the
 * close-mid-call guard (browser prompt, best-effort BYE + relay hangup on pagehide).
 */
import { signal } from '@preact/signals';
import { type TimerHost, realTimers } from '../keepalive/timers.js';

export interface LifecycleDeps {
  doc?: Document;
  win?: Window;
  timers?: TimerHost;
  now?: () => number;
  /** no input for this long -> idle (ms) */
  idleAfterMs?: number;
  /** a timer gap longer than this means the machine slept or the tab was frozen */
  resumeGapMs?: number;
  onResume?(gapMs: number): void;
  onOnline?(): void;
  onVisibility?(v: 'visible' | 'hidden'): void;
}

export class Lifecycle {
  readonly visibility = signal<'visible' | 'hidden'>('visible');
  readonly idle = signal(false);
  readonly locked = signal(false);
  private lastInput: number;
  private lastTick: number;
  private tick: unknown = null;
  private readonly cleanups: Array<() => void> = [];
  private readonly timers: TimerHost;
  private readonly now: () => number;

  constructor(private readonly d: LifecycleDeps = {}) {
    this.timers = d.timers ?? realTimers;
    this.now = d.now ?? Date.now;
    this.lastInput = this.now();
    this.lastTick = this.now();
  }

  start(): void {
    const doc = this.d.doc ?? document;
    const win = this.d.win ?? window;
    const onVis = (): void => {
      const v = doc.visibilityState === 'hidden' ? 'hidden' : 'visible';
      this.visibility.value = v;
      this.d.onVisibility?.(v);
    };
    onVis();
    doc.addEventListener('visibilitychange', onVis);
    this.cleanups.push(() => doc.removeEventListener('visibilitychange', onVis));
    const onInput = (): void => {
      this.lastInput = this.now();
      if (this.idle.value) this.idle.value = false;
    };
    for (const ev of ['mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel'] as const) {
      doc.addEventListener(ev, onInput, { passive: true });
      this.cleanups.push(() => doc.removeEventListener(ev, onInput));
    }
    const onOnline = (): void => this.d.onOnline?.();
    win.addEventListener('online', onOnline);
    this.cleanups.push(() => win.removeEventListener('online', onOnline));
    this.tick = this.timers.setInterval(() => {
      const t = this.now();
      const gap = t - this.lastTick;
      this.lastTick = t;
      if (gap > (this.d.resumeGapMs ?? 10_000)) this.d.onResume?.(gap);
      const idle = t - this.lastInput > (this.d.idleAfterMs ?? 5 * 60_000);
      if (idle !== this.idle.value) this.idle.value = idle;
    }, 1000);
    void this.watchIdleDetector();
  }

  /** screen lock through the Idle Detection API when the permission is already granted */
  private async watchIdleDetector(): Promise<void> {
    const IdleDetectorCtor = (globalThis as { IdleDetector?: new () => { screenState: 'locked' | 'unlocked'; addEventListener(t: 'change', cb: () => void): void; start(o: { threshold: number }): Promise<void> } }).IdleDetector;
    const perms = (navigator as Navigator & { permissions?: Permissions }).permissions;
    if (!IdleDetectorCtor || !perms) return;
    try {
      const status = await perms.query({ name: 'idle-detection' as PermissionName });
      if (status.state !== 'granted') return;
      const det = new IdleDetectorCtor();
      det.addEventListener('change', () => {
        this.locked.value = det.screenState === 'locked';
      });
      await det.start({ threshold: 60_000 });
    } catch {
      /* unsupported or denied: locked stays false */
    }
  }

  stop(): void {
    for (const c of this.cleanups.splice(0)) c();
    if (this.tick !== null) this.timers.clearInterval(this.tick);
    this.tick = null;
  }
}

/** window closed mid-call: prompt, then best-effort teardown on pagehide */
export function guardClose(win: Window, hasLines: () => boolean, teardown: () => void): () => void {
  const onBeforeUnload = (ev: BeforeUnloadEvent): void => {
    if (!hasLines()) return;
    ev.preventDefault();
    ev.returnValue = '';
  };
  const onPageHide = (): void => teardown();
  win.addEventListener('beforeunload', onBeforeUnload);
  win.addEventListener('pagehide', onPageHide);
  return () => {
    win.removeEventListener('beforeunload', onBeforeUnload);
    win.removeEventListener('pagehide', onPageHide);
  };
}
